import { useState, useContext, createContext, useEffect, useCallback } from "react";

const themeContext = createContext();

const THEME_OPTIONS = {
  LIGHT: "light",
  DARK: "dark",
  SYSTEM: "system",
};

function getSystemTheme() {
  if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
    return THEME_OPTIONS.DARK;
  }
  return THEME_OPTIONS.LIGHT;
}

function useThemeProvider() {
  const [themePreference, setThemePreference] = useState(() => localStorage.getItem("themePreference") || THEME_OPTIONS.SYSTEM);

  const [activeTheme, setActiveTheme] = useState(() => {
    const preference = localStorage.getItem("themePreference") || THEME_OPTIONS.SYSTEM;

    if (preference === THEME_OPTIONS.SYSTEM) {
      return getSystemTheme();
    }
    return preference;
  });

  const updateActiveTheme = useCallback((preference) => {
    if (preference === THEME_OPTIONS.SYSTEM) {
      setActiveTheme(getSystemTheme());
    } else {
      setActiveTheme(preference);
    }
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", activeTheme);
  }, [activeTheme]);

  useEffect(() => {
    if (themePreference !== THEME_OPTIONS.SYSTEM) return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      setActiveTheme(getSystemTheme());
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [themePreference]);

  const changeTheme = useCallback(
    (newPreference) => {
      setThemePreference(newPreference);
      localStorage.setItem("themePreference", newPreference);
      updateActiveTheme(newPreference);
    },
    [updateActiveTheme]
  );

  const toggleTheme = useCallback(() => {
    const newTheme = activeTheme === THEME_OPTIONS.LIGHT ? THEME_OPTIONS.DARK : THEME_OPTIONS.LIGHT;
    changeTheme(newTheme);
  }, [activeTheme, changeTheme]);

  const initializeFromProfile = useCallback(
    (userThemePreference) => {
      if (userThemePreference) {
        setThemePreference(userThemePreference);
        localStorage.setItem("themePreference", userThemePreference);
        updateActiveTheme(userThemePreference);
      }
    },
    [updateActiveTheme]
  );

  return {
    themePreference,
    activeTheme,
    changeTheme,
    toggleTheme,
    initializeFromProfile,
    isDark: activeTheme === THEME_OPTIONS.DARK,
    THEME_OPTIONS,
  };
}

export function ProvideTheme({ children }) {
  const values = useThemeProvider();
  return <themeContext.Provider value={values}>{children}</themeContext.Provider>;
}

export const useTheme = () => {
  return useContext(themeContext);
};

export { THEME_OPTIONS };
